import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FiPlus, FiEdit2, FiTrash2, FiChevronDown, FiChevronRight, FiDollarSign, FiCalendar } from 'react-icons/fi';
import tesoreriaService from '../services/tesoreriaService';
import planCuentasService from '../services/planCuentasService';
import Loading from '../components/Loading';
import './CuentasBancariasPage.css';

const ensureArray = (v) => Array.isArray(v) ? v : [];

const CuentasBancariasPage = () => {
  const [cuentas, setCuentas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [movimientos, setMovimientos] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCuenta, setEditingCuenta] = useState(null);
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroBanco, setFiltroBanco] = useState('');
  const [filtroActivo, setFiltroActivo] = useState('');
  const [saldoFechaModal, setSaldoFechaModal] = useState({ open: false, cuentaId: null, fecha: '', saldo: null });
  const [planCuentas, setPlanCuentas] = useState([]);

  const [form, setForm] = useState({
    nombre: '', tipo: 'cuenta_corriente', banco: '', numero_cuenta: '',
    moneda: 'CLP', saldo_inicial: 0, plan_cuenta: '', activo: true,
  });

  useEffect(() => {
    cargarCuentas();
    cargarPlanCuentas();
  }, []);

  const cargarPlanCuentas = async () => {
    try {
      const res = await planCuentasService.getAll();
      setPlanCuentas(ensureArray(res.data.results || res.data));
    } catch { /* silencioso */ }
  };

  const cargarCuentas = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (filtroTipo) filters.tipo = filtroTipo;
      if (filtroBanco) filters.banco = filtroBanco;
      if (filtroActivo !== '') filters.activo = filtroActivo;
      const res = await tesoreriaService.cuentasBancarias.getAll(filters);
      setCuentas(ensureArray(res.data.results || res.data));
    } catch (err) {
      toast.error('Error al cargar cuentas bancarias');
    } finally { setLoading(false); }
  };

  useEffect(() => { cargarCuentas(); }, [filtroTipo, filtroBanco, filtroActivo]);

  const toggleExpand = async (id) => {
    if (expandedId === id) { setExpandedId(null); return; }
    if (!movimientos[id]) {
      try {
        const res = await tesoreriaService.cuentasBancarias.getMovimientos(id);
        setMovimientos(prev => ({ ...prev, [id]: ensureArray(res.data.results || res.data) }));
      } catch { toast.error('Error al cargar movimientos'); return; }
    }
    setExpandedId(id);
  };

  const handleSave = async () => {
    try {
      const data = { ...form, plan_cuenta: form.plan_cuenta || null };
      if (editingCuenta) {
        await tesoreriaService.cuentasBancarias.update(editingCuenta.id, data);
        toast.success('Cuenta actualizada');
      } else {
        await tesoreriaService.cuentasBancarias.create(data);
        toast.success('Cuenta creada');
      }
      setModalOpen(false);
      setEditingCuenta(null);
      resetForm();
      cargarCuentas();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta cuenta bancaria?')) return;
    try {
      await tesoreriaService.cuentasBancarias.delete(id);
      toast.success('Cuenta eliminada');
      cargarCuentas();
    } catch { toast.error('Error al eliminar'); }
  };

  const handleEdit = (cuenta) => {
    setEditingCuenta(cuenta);
    setForm({
      nombre: cuenta.nombre, tipo: cuenta.tipo, banco: cuenta.banco || '',
      numero_cuenta: cuenta.numero_cuenta || '', moneda: cuenta.moneda || 'CLP',
      saldo_inicial: cuenta.saldo_inicial, plan_cuenta: cuenta.plan_cuenta || '',
      activo: cuenta.activo,
    });
    setModalOpen(true);
  };

  const resetForm = () => {
    setForm({ nombre: '', tipo: 'cuenta_corriente', banco: '', numero_cuenta: '', moneda: 'CLP', saldo_inicial: 0, plan_cuenta: '', activo: true });
  };

  const consultarSaldoFecha = async () => {
    try {
      const res = await tesoreriaService.cuentasBancarias.getSaldoAFecha(saldoFechaModal.cuentaId, saldoFechaModal.fecha);
      setSaldoFechaModal(prev => ({ ...prev, saldo: res.data.saldo }));
    } catch { toast.error('Error al consultar saldo'); }
  };

  const formatCurrency = (value, moneda = 'CLP') => {
    return new Intl.NumberFormat('es-CL', { style: 'currency', currency: moneda === 'UF' ? 'CLP' : moneda }).format(value || 0);
  };

  const getTipoBadge = (tipo) => {
    const map = {
      cuenta_corriente: { label: 'Cta. Corriente', cls: 'badge-blue' },
      cuenta_ahorro: { label: 'Cta. Ahorro', cls: 'badge-green' },
      caja_chica: { label: 'Caja Chica', cls: 'badge-orange' },
      caja_general: { label: 'Caja General', cls: 'badge-purple' },
    };
    return map[tipo] || { label: tipo, cls: 'badge-default' };
  };

  if (loading && cuentas.length === 0) return <Loading />;

  return (
    <div className="cuentas-bancarias-page">
      <div className="page-header">
        <h1>Cuentas Bancarias y Cajas</h1>
        <button className="btn-primary" onClick={() => { resetForm(); setEditingCuenta(null); setModalOpen(true); }}>
          <FiPlus /> Nueva Cuenta
        </button>
      </div>

      {/* Stats */}
      <div className="estadisticas-grid">
        <div className="stat-card" style={{ borderLeftColor: '#3b82f6' }}>
          <div className="stat-label">Total Cuentas</div>
          <div className="stat-value">{cuentas.length}</div>
        </div>
        <div className="stat-card" style={{ borderLeftColor: '#10b981' }}>
          <div className="stat-label">Saldo Total CLP</div>
          <div className="stat-value">{formatCurrency(cuentas.filter(c => c.moneda === 'CLP').reduce((s, c) => s + parseFloat(c.saldo_actual || 0), 0))}</div>
        </div>
        <div className="stat-card" style={{ borderLeftColor: '#f59e0b' }}>
          <div className="stat-label">Cuentas Activas</div>
          <div className="stat-value">{cuentas.filter(c => c.activo).length}</div>
        </div>
      </div>

      {/* Filtros */}
      <div className="filtros-container">
        <select className="select-filtro" value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}>
          <option value="">Todos los tipos</option>
          <option value="cuenta_corriente">Cuenta Corriente</option>
          <option value="cuenta_ahorro">Cuenta de Ahorro</option>
          <option value="caja_chica">Caja Chica</option>
          <option value="caja_general">Caja General</option>
        </select>
        <input className="input-filtro" placeholder="Filtrar por banco..." value={filtroBanco} onChange={e => setFiltroBanco(e.target.value)} />
        <select className="select-filtro" value={filtroActivo} onChange={e => setFiltroActivo(e.target.value)}>
          <option value="">Todas</option>
          <option value="true">Activas</option>
          <option value="false">Inactivas</option>
        </select>
      </div>

      {/* Tabla */}
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ width: 40 }}></th>
              <th>Nombre</th>
              <th>Tipo</th>
              <th>Banco</th>
              <th>N° Cuenta</th>
              <th>Moneda</th>
              <th style={{ textAlign: 'right' }}>Saldo Actual</th>
              <th>Estado</th>
              <th style={{ textAlign: 'right' }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {cuentas.length === 0 ? (
              <tr><td colSpan="9" className="empty-state"><p>No hay cuentas bancarias registradas</p></td></tr>
            ) : cuentas.map(cuenta => (
              <React.Fragment key={cuenta.id}>
                <tr className={expandedId === cuenta.id ? 'expanded' : ''}>
                  <td>
                    <button className="btn-icon" onClick={() => toggleExpand(cuenta.id)}>
                      {expandedId === cuenta.id ? <FiChevronDown /> : <FiChevronRight />}
                    </button>
                  </td>
                  <td><strong>{cuenta.nombre}</strong></td>
                  <td><span className={`badge ${getTipoBadge(cuenta.tipo).cls}`}>{getTipoBadge(cuenta.tipo).label}</span></td>
                  <td>{cuenta.banco || '-'}</td>
                  <td className="numero-cuenta">{cuenta.numero_cuenta || '-'}</td>
                  <td>{cuenta.moneda}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600, fontFamily: "'Courier New', monospace" }}>
                    {formatCurrency(cuenta.saldo_actual, cuenta.moneda)}
                  </td>
                  <td><span className={`badge ${cuenta.activo ? 'badge-green' : 'badge-gray'}`}>{cuenta.activo ? 'Activa' : 'Inactiva'}</span></td>
                  <td>
                    <div className="acciones-cell">
                      <button className="btn-icon" title="Saldo a fecha" onClick={() => setSaldoFechaModal({ open: true, cuentaId: cuenta.id, fecha: '', saldo: null })}>
                        <FiCalendar />
                      </button>
                      <button className="btn-icon" title="Editar" onClick={() => handleEdit(cuenta)}><FiEdit2 /></button>
                      <button className="btn-icon btn-danger" title="Eliminar" onClick={() => handleDelete(cuenta.id)}><FiTrash2 /></button>
                    </div>
                  </td>
                </tr>
                {expandedId === cuenta.id && (
                  <tr className="detalles-row">
                    <td colSpan="9">
                      <div className="detalles-container">
                        <h4>Movimientos</h4>
                        {movimientos[cuenta.id]?.length > 0 ? (
                          <table className="detalles-table">
                            <thead>
                              <tr><th>Fecha</th><th>Tipo</th><th>Concepto</th><th style={{ textAlign: 'right' }}>Monto</th><th style={{ textAlign: 'right' }}>Saldo</th><th>Conciliado</th></tr>
                            </thead>
                            <tbody>
                              {movimientos[cuenta.id].map((mov, i) => (
                                <tr key={i}>
                                  <td>{mov.fecha}</td>
                                  <td><span className={`badge ${mov.tipo === 'ingreso' ? 'badge-green' : 'badge-red'}`}>{mov.tipo_display || mov.tipo}</span></td>
                                  <td>{mov.concepto}</td>
                                  <td style={{ textAlign: 'right', fontFamily: "'Courier New', monospace", color: mov.tipo === 'ingreso' || mov.tipo === 'transferencia_entrada' ? '#059669' : '#dc2626' }}>
                                    {formatCurrency(mov.monto, cuenta.moneda)}
                                  </td>
                                  <td style={{ textAlign: 'right', fontFamily: "'Courier New', monospace" }}>{formatCurrency(mov.saldo_posterior, cuenta.moneda)}</td>
                                  <td>{mov.conciliado ? <span className="badge badge-green">Si</span> : <span className="badge badge-gray">No</span>}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        ) : <p className="text-muted">Sin movimientos registrados</p>}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal Crear/Editar */}
      {modalOpen && (
        <div className="modal-overlay" onClick={() => setModalOpen(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>{editingCuenta ? 'Editar Cuenta Bancaria' : 'Nueva Cuenta Bancaria'}</h2>
            <div className="form-grid">
              <div className="form-group">
                <label>Nombre *</label>
                <input value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })} placeholder="Ej: Banco Estado CTA CTE" />
              </div>
              <div className="form-group">
                <label>Tipo *</label>
                <select value={form.tipo} onChange={e => setForm({ ...form, tipo: e.target.value })}>
                  <option value="cuenta_corriente">Cuenta Corriente</option>
                  <option value="cuenta_ahorro">Cuenta de Ahorro</option>
                  <option value="caja_chica">Caja Chica</option>
                  <option value="caja_general">Caja General</option>
                </select>
              </div>
              <div className="form-group">
                <label>Banco</label>
                <input value={form.banco} onChange={e => setForm({ ...form, banco: e.target.value })} placeholder="Ej: Banco Estado" />
              </div>
              <div className="form-group">
                <label>N° Cuenta</label>
                <input value={form.numero_cuenta} onChange={e => setForm({ ...form, numero_cuenta: e.target.value })} placeholder="N° de cuenta" />
              </div>
              <div className="form-group">
                <label>Moneda</label>
                <select value={form.moneda} onChange={e => setForm({ ...form, moneda: e.target.value })}>
                  <option value="CLP">CLP</option>
                  <option value="USD">USD</option>
                  <option value="UF">UF</option>
                </select>
              </div>
              <div className="form-group">
                <label>Saldo Inicial</label>
                <input type="number" value={form.saldo_inicial} onChange={e => setForm({ ...form, saldo_inicial: e.target.value })} />
              </div>
              <div className="form-group" style={{ gridColumn: 'span 2' }}>
                <label>Cuenta Contable (Plan de Cuentas)</label>
                <select value={form.plan_cuenta} onChange={e => setForm({ ...form, plan_cuenta: e.target.value })}>
                  <option value="">Sin cuenta contable asignada</option>
                  {planCuentas.map(pc => (
                    <option key={pc.id} value={pc.id}>{pc.codigo} - {pc.nombre}</option>
                  ))}
                </select>
                <small style={{ color: '#6b7280', fontSize: 12 }}>Requerida para generar asientos contables automaticos al ejecutar pagos</small>
              </div>
              <div className="form-group form-check">
                <label><input type="checkbox" checked={form.activo} onChange={e => setForm({ ...form, activo: e.target.checked })} /> Cuenta Activa</label>
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setModalOpen(false)}>Cancelar</button>
              <button className="btn-primary" onClick={handleSave}><FiDollarSign /> {editingCuenta ? 'Actualizar' : 'Crear'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Saldo a Fecha */}
      {saldoFechaModal.open && (
        <div className="modal-overlay" onClick={() => setSaldoFechaModal({ open: false, cuentaId: null, fecha: '', saldo: null })}>
          <div className="modal-content modal-sm" onClick={e => e.stopPropagation()}>
            <h2>Consultar Saldo a Fecha</h2>
            <div className="form-group">
              <label>Fecha</label>
              <input type="date" value={saldoFechaModal.fecha} onChange={e => setSaldoFechaModal(prev => ({ ...prev, fecha: e.target.value, saldo: null }))} />
            </div>
            {saldoFechaModal.saldo !== null && (
              <div className="saldo-resultado">
                <span>Saldo al {saldoFechaModal.fecha}:</span>
                <strong>{formatCurrency(saldoFechaModal.saldo)}</strong>
              </div>
            )}
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setSaldoFechaModal({ open: false, cuentaId: null, fecha: '', saldo: null })}>Cerrar</button>
              <button className="btn-primary" onClick={consultarSaldoFecha} disabled={!saldoFechaModal.fecha}>Consultar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CuentasBancariasPage;
